import { useEffect, useState } from 'react';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area,
  PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useFund } from '../context/FundContext';
import FundDateBar from '../components/FundDateBar';
import { Loading } from '../components/Loader';
import { api } from '../api';
import { money, shortName } from '../format';

const DONUT_COLORS = ['#3fb6c9', '#5b6ee1', '#bb6bd9', '#4caf7d', '#f2994a', '#e57373', '#9575cd', '#90a4ae'];

function pct(v, digits = 2) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  return `${Number(v).toFixed(digits)}%`;
}
function Arrow({ up }) {
  return <span className={`arrow ${up ? 'up' : 'down'}`}>{up ? '▲' : '▼'}</span>;
}

/* ---- Hero cards (top row) ---- */
function HeroBars({ title, value, sub, data, gradient }) {
  return (
    <div className="hero-card" style={{ background: gradient }}>
      <div className="hero-title">{title}</div>
      <div className="hero-value">{value}</div>
      <div className="hero-sub">{sub}</div>
      <div className="hero-chart">
        <ResponsiveContainer width="100%" height={70}>
          <BarChart data={data}>
            <Bar dataKey="v" fill="rgba(255,255,255,0.85)" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
function HeroLine({ title, value, sub, data, gradient }) {
  return (
    <div className="hero-card" style={{ background: gradient }}>
      <div className="hero-title">{title}</div>
      <div className="hero-value">{value}</div>
      <div className="hero-sub">{sub}</div>
      <div className="hero-chart">
        <ResponsiveContainer width="100%" height={70}>
          <LineChart data={data}>
            <Line type="monotone" dataKey="v" stroke="#fff" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { fund, date } = useFund();
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!fund || !date) return undefined;
    // Guard against out-of-order responses: when fund/date changes quickly,
    // several requests can be in flight. Only the latest one is allowed to
    // update state, so a slow stale request can't clobber fresh data (or leave
    // a stale error banner next to a newer fund's charts).
    let active = true;
    setLoading(true);
    setError(null);
    api.dashboard(fund, date)
      .then((data) => { if (active) { setD(data); setError(null); } })
      .catch((e) => { if (active) { setError(e.message); setD(null); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [fund, date]);

  return (
    <div>
      <FundDateBar />
      {error && <div className="error">Error: {error}</div>}
      {loading && <Loading label="Loading dashboard…" />}
      {d && !loading && d.hasPerformance === false && (
        <div className="notice">
          No daily performance series is available for this share class — showing
          balance-sheet figures (AUM, holdings) only.
        </div>
      )}

      {d && !loading && (
        <>
          {/* ---- Hero row ---- */}
          <div className="hero-grid">
            <HeroBars
              title="NAV - Daily (USD)"
              value={money(d.cards.navChange)}
              sub="compared to previous day"
              gradient="linear-gradient(135deg,#5b6ee1,#3f4eae)"
              data={d.navSeries.slice(-30).map((x) => ({ v: x.nav }))}
            />
            <HeroLine
              title="Rate of Return - Daily"
              value={pct(d.cards.rorDaily * 100)}
              sub="compared to previous day"
              gradient="linear-gradient(135deg,#2bb7e6,#1f8fc0)"
              data={d.rorSeries.slice(-30).map((x) => ({ v: x.ror }))}
            />
            <HeroLine
              title="VAMI - Daily"
              value={d.cards.vamiChange >= 0 ? `↑ ${d.cards.vamiChange.toFixed(2)}` : `↓ ${Math.abs(d.cards.vamiChange).toFixed(2)}`}
              sub="compared to previous day"
              gradient="linear-gradient(135deg,#4caf7d,#3a8f63)"
              data={d.vamiSeries.slice(-30).map((x) => ({ v: x.vami }))}
            />
            <HeroBars
              title="Month End NAV (USD)"
              value={money(d.cards.navDaily)}
              sub={`as of ${d.date}`}
              gradient="linear-gradient(135deg,#1aa39a,#157f78)"
              data={d.monthEndNav.map((x) => ({ v: x.nav }))}
            />
          </div>

          {/* ---- Middle row ---- */}
          <div className="mid-grid">
            <div className="panel stats-panel">
              <div className="stat-strip">
                <Stat label="Additions (USD)" value={money(d.cards.additions)} />
                <Stat label="Redemptions (USD)" value={money(d.cards.redemptions)} />
                <Stat label="Management Fees (USD)" value={money(d.cards.managementFees)} />
                <Stat label="Incentive Fees (USD)" value={money(d.cards.incentiveFees)} />
                <Stat label="Net P&L (USD)" value={money(d.cards.netPnl)} />
              </div>
              <div className="trend-head">Net P&amp;L — Month to Date</div>
              <ResponsiveContainer width="100%" height={230}>
                <LineChart data={d.netPnlTrend.map((x) => ({ date: x.date.slice(5), v: x.netPerf }))}
                  margin={{ top: 10, right: 16, left: 0, bottom: 4 }}>
                  <XAxis dataKey="date" fontSize={11} />
                  <YAxis fontSize={11} width={56} tickFormatter={(v) => money(v)} />
                  <Tooltip formatter={(v) => money(v)} />
                  <Line type="monotone" dataKey="v" stroke="#2f6fed" strokeWidth={2} dot={{ r: 3, fill: '#2f6fed' }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="panel pvnc-panel">
              <h3>Portfolio Value vs Net Contribution</h3>
              <div className="ror-row">
                <div>
                  <div className="ror-label">DAILY ROR</div>
                  <div className="ror-value">{pct(d.cards.rorDaily * 100)} <Arrow up={d.cards.rorDaily >= 0} /></div>
                </div>
                <div>
                  <div className="ror-label">MONTHLY ROR</div>
                  <div className="ror-value">{pct(d.cards.mtdRor * 100)} <Arrow up={d.cards.mtdRor >= 0} /></div>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={120}>
                <AreaChart data={d.navSeries.slice(-40).map((x) => ({ v: x.nav }))}>
                  <Area type="monotone" dataKey="v" stroke="#7cb342" fill="#aed581" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="hero-card aum-card" style={{ background: 'linear-gradient(135deg,#1aa39a,#147a72)' }}>
              <div className="aum-value">{money(d.cards.aum)}</div>
              <div className="aum-label">AUM (USD)</div>
            </div>

            <div className="hero-card mpr-card" style={{ background: 'linear-gradient(135deg,#4a7ef0,#3866d6)' }}>
              <div className="hero-title">Multi Period Returns</div>
              <div className="hero-sub">{pct(d.cards.ytdRor * 100)} YTD</div>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={d.multiPeriod.map((x) => ({ date: x.date.slice(5), net: x.net, gross: x.gross }))}>
                  <Line type="monotone" dataKey="gross" stroke="#ffd180" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="net" stroke="#fff" strokeWidth={2} dot={false} />
                  <Tooltip formatter={(v) => pct(v)} contentStyle={{ fontSize: 12 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* ---- Bottom row ---- */}
          <div className="bottom-grid">
            <div className="panel">
              <h3>Holdings (USD) {d.positionDate && <span className="muted-inline">as of {d.positionDate}</span>}</h3>
              {d.holdings.length ? (
                <div className="holdings-wrap">
                  <ResponsiveContainer width={220} height={220}>
                    <PieChart>
                      <Pie data={d.holdings.slice(0, 8)} dataKey="mv" nameKey="security" innerRadius={60} outerRadius={100}>
                        {d.holdings.slice(0, 8).map((_, i) => <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => money(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="holdings-legend">
                    <div className="holdings-total">{money(d.cards.totalHoldings)}<span>Total Holdings</span></div>
                    {d.holdings.slice(0, 6).map((h, i) => (
                      <div className="legend-row" key={h.security}>
                        <span className="legend-dot" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                        <span className="legend-name">{shortName(h.security)}</span>
                        <span className="legend-val">{money(h.mv)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : <p className="muted">No positions for this date.</p>}
            </div>

            <div className="panel">
              <h3>Top Leaders</h3>
              <table className="table">
                <thead><tr><th>Securities</th><th className="num">Gain (USD)</th></tr></thead>
                <tbody>
                  {d.leaders.length ? d.leaders.map((l) => (
                    <tr key={l.security}><td>{shortName(l.security)}</td><td className="num gain">{money(l.gl)}</td></tr>
                  )) : <tr><td colSpan={2} className="muted">No data</td></tr>}
                </tbody>
              </table>
            </div>

            <div className="panel">
              <h3>Top Laggers</h3>
              <table className="table">
                <thead><tr><th>Securities</th><th className="num">Loss (USD)</th></tr></thead>
                <tbody>
                  {d.laggers.length ? d.laggers.map((l) => (
                    <tr key={l.security}><td>{shortName(l.security)}</td><td className="num loss">{money(l.gl)}</td></tr>
                  )) : <tr><td colSpan={2} className="muted">No data</td></tr>}
                </tbody>
              </table>
            </div>

            <div className="panel">
              <h3>Portfolio — Allocation by Asset Class</h3>
              {d.assetAllocation.length ? (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={d.assetAllocation.map((a) => ({ ...a, v: Math.abs(a.mv) }))}
                      dataKey="v" nameKey="assetClass" outerRadius={100} label={(e) => e.assetClass}>
                      {d.assetAllocation.map((_, i) => <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => money(v)} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="muted">No positions for this date.</p>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
